import { useState } from 'react';
import { ATTRIBUTES } from '../../lib/attributes';
import { getChartType } from '../../lib/charts';
import type { PopulationBlob } from '../../lib/blobLoader';
import type { Widget } from '../../lib/dashboards';
import { useBlobStore } from '../../state/blobStore';

const EMPTY_INDICES = new Uint32Array();
import {
  useDashboardIndices,
  useDashboardStore,
} from '../../state/dashboardStore';

type Props = {
  dashboardId: string;
  widget: Widget;
  canMoveUp: boolean;
  canMoveDown: boolean;
};

export default function DashboardWidget({
  dashboardId,
  widget,
  canMoveUp,
  canMoveDown,
}: Props) {
  const blob = useBlobStore((s) => s.blob);
  const filtered = useDashboardIndices(dashboardId);
  const updateWidget = useDashboardStore((s) => s.updateWidget);
  const removeWidget = useDashboardStore((s) => s.removeWidget);
  const moveWidget = useDashboardStore((s) => s.moveWidget);
  const [configOpen, setConfigOpen] = useState(false);

  const chartType = getChartType(widget.chartTypeId);
  const Renderer = chartType?.render ?? null;
  const label = widget.title || chartType?.label || widget.chartTypeId;
  const hasSlots = (chartType?.slots.length ?? 0) > 0;
  const slotsFilled =
    !chartType || chartType.slots.every((s) => widget.slots[s.name]);
  const requiresData = chartType?.requiresData !== false;

  const handleSlotChange = (slotName: string, value: string) => {
    updateWidget(dashboardId, widget.id, {
      slots: { ...widget.slots, [slotName]: value || null },
    });
  };

  return (
    <>
      <div className="dashboard-widget__handle">
        <span className="dashboard-widget__title">{label}</span>
        <div className="dashboard-widget__handle-actions">
          <button
            type="button"
            className="dashboard-widget__action"
            onClick={() => moveWidget(dashboardId, widget.id, 'up')}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={!canMoveUp}
            aria-label={`Move ${label} up`}
          >
            ↑
          </button>
          <button
            type="button"
            className="dashboard-widget__action"
            onClick={() => moveWidget(dashboardId, widget.id, 'down')}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={!canMoveDown}
            aria-label={`Move ${label} down`}
          >
            ↓
          </button>
          {hasSlots && (
            <button
              type="button"
              className="dashboard-widget__action"
              onClick={() => setConfigOpen((v) => !v)}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={configOpen ? 'Hide widget settings' : 'Configure widget'}
              aria-expanded={configOpen}
            >
              ⚙
            </button>
          )}
          <button
            type="button"
            className="dashboard-widget__delete"
            onClick={() => removeWidget(dashboardId, widget.id)}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={`Remove ${label}`}
          >
            ×
          </button>
        </div>
      </div>

      {configOpen && chartType && hasSlots && (
        <div
          className="dashboard-widget__config"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {chartType.slots.map((slot) => {
            const options = ATTRIBUTES.filter((a) =>
              slot.accepts.includes(a.kind),
            );
            const value = widget.slots[slot.name] ?? '';
            return (
              <label key={slot.name} className="dashboard-widget__slot">
                <span className="dashboard-widget__slot-label">{slot.name}</span>
                <select
                  className="dashboard-widget__slot-select"
                  value={value}
                  onChange={(e) => handleSlotChange(slot.name, e.target.value)}
                >
                  <option value="">— pick attribute —</option>
                  {options.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
      )}

      <div className="dashboard-widget__body">
        {!chartType ? (
          <p className="chart-card__empty">
            Unknown chart type: {widget.chartTypeId}.
          </p>
        ) : requiresData && (!blob || !filtered) ? (
          <p className="chart-card__empty">Waiting for population to load…</p>
        ) : !slotsFilled ? (
          <p className="chart-card__empty">
            {chartType.slots.length === 1
              ? 'Pick an attribute in the widget settings.'
              : 'Pick attributes in the widget settings.'}
          </p>
        ) : Renderer ? (
          <Renderer
            blob={blob as PopulationBlob}
            indices={filtered?.indices ?? EMPTY_INDICES}
            slots={widget.slots}
            widget={widget}
            dashboardId={dashboardId}
          />
        ) : (
          <p className="chart-card__empty">No renderer for {chartType.id}.</p>
        )}
      </div>
    </>
  );
}
